import { Model, Attr, BelongsTo } from 'spraypaint'
import moment from 'moment'

import ApplicationRecord from '@/models/ApplicationRecord'
import { Customer, DotationOperation, CustomerOrder, Caretaker, MultiChoiceGiftCard, Gift } from '@/models/index'
import request from '@/utils/request'

@Model()
export default class MultiChoiceCard extends ApplicationRecord {
  static jsonapiType = 'multi_choice_cards'

  @Attr() id!: string
  @Attr() name!: string
  @Attr() amount!: number
  @Attr() amountCents!: number
  @Attr() usedAmountCents!: number
  @Attr() remainingAmount!: number
  @Attr() expireAt!: string
  @Attr() usedAt!: string
  @Attr() createdAt!: string
  @Attr() activateAt!: string
  @Attr() scheduledAt!: string | null
  @Attr() status!: string
  @Attr() extraExpirationDuration!: number | null
  @Attr() extraExpirationGrantedAt!: string | null
  @Attr() multiChoiceCardDistributions!: any[]

  @BelongsTo() source!: Caretaker | CustomerOrder
  @BelongsTo() customer!: Customer
  @BelongsTo() dotationOperation!: DotationOperation
  @BelongsTo() customerOrder!: CustomerOrder
  @BelongsTo() multiChoiceGiftCard!: MultiChoiceGiftCard
  @BelongsTo() gift!: Gift

  get isUsed(): boolean {
    return this.status === 'used' || !!this.usedAt
  }

  get isExpired(): boolean {
    if (this.status === 'expired') return true
    if (!this.expireAt) return false
    return new Date(this.expireAt) < new Date()
  }

  get isBlankCard(): boolean {
    return !this.customer?.id
  }

  get isCardUsed(): boolean {
    const usedAmount = this.usedAmountCents ?? 0
    return usedAmount > 0 || this.status === 'used' || !!this.usedAt
  }

  get canExtendExpiration(): boolean {
    if (this.isBlankCard) return false
    return !this.isCardUsed
  }

  get isActiveForExtension(): boolean {
    if (!this.expireAt) return false
    return moment(this.expireAt).isAfter(moment(), 'day')
  }

  get canRevoke(): boolean {
    return !this.isCardUsed
  }

  get hasDeliveryEmailBeenSent(): boolean {
    if (!this.customer?.id) return false
    if (!this.scheduledAt) return true
    return moment(this.scheduledAt).isSameOrBefore(moment(), 'day')
  }

  public static async extendDurations(duration: number, multiChoiceCardIds: string[]): Promise<void> {
    await request.put('/market/v1/multi_choice_cards/extend_durations', {
      data: {
        duration,
        multi_choice_card_ids: multiChoiceCardIds
      }
    })
  }

  public async extendExpiration(durationDays: number): Promise<MultiChoiceCard> {
    const params = {
      data: {
        type: 'multi_choice_cards',
        id: this.id,
        attributes: {
          extra_expiration_duration: durationDays
        }
      }
    }

    const response = await request.put(`/market/v1/multi_choice_cards/${this.id}`, params)
    const updated = new MultiChoiceCard()
    updated.fromJsonapi(response.data, response)

    return updated
  }

  public getColorByName() {
    let name = ''
    let hash = 0

    if (this.customer?.firstname && this.customer?.lastname) name = this.customer.firstname + this.customer.lastname

    if (name.length === 0) return hash
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
      hash = hash & hash
    }
    let color = '#'
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 255
      color += ('00' + value.toString(16)).substr(-2)
    }
    return color
  }
}
